/**
 * Route paths configuration - Single source of truth for all shop-specific paths
 * This file is used both at build time (router module) and runtime (routes.ts)
 * DO NOT import anything that depends on .nuxt directory here
 * NOTE: Use relative imports here because this file is loaded at build time
 *       before Nuxt's alias resolver is available
 */

import { Shops } from './shops'

export interface RoutePathConfig {
  name: string
  componentPath?: string // Path relative to project root, e.g., 'pages/orders/index.vue'
  paths: Partial<Record<Shops, string>>
  aliases?: Partial<Record<Shops, string>>
  condition?: () => boolean
}

/**
 * All routes with their shop-specific paths
 * Routes with componentPath will be handled by Nuxt (SSR)
 * Routes without componentPath are external (monolith) routes
 */
export const ROUTE_PATHS: Record<string, RoutePathConfig> = {
  homepage: {
    name: 'homepage',
    componentPath:
      process.env.SHOW_DEBUG_HOMEPAGE === 'true'
        ? 'pages/homepage/index.vue'
        : undefined,
    paths: {
      [Shops.CZ10MA]: '',
      [Shops.HR10MA]: '',
      [Shops.HU20MA]: '',
      [Shops.PL20MA]: '',
      [Shops.SI10SI]: '',
      [Shops.SK10MA]: '',
    },
    aliases: {
      [Shops.CZ10MA]: 'cz-homepage-alias',
      [Shops.HR10MA]: 'hr-homepage-alias',
      [Shops.HU20MA]: 'hu-homepage-alias',
      [Shops.PL20MA]: 'pl-homepage-alias',
      [Shops.SI10SI]: 'si-homepage-alias',
      [Shops.SK10MA]: 'sk-homepage-alias',
    },
  },
  campaign: {
    name: 'campaign',
    paths: {
      [Shops.CZ10MA]: 'kampan/:campaignId',
      [Shops.HR10MA]: 'kampanja/:campaignId',
      [Shops.HU20MA]: 'kampany/:campaignId',
      [Shops.PL20MA]: 'kampania/:campaignId',
      [Shops.SI10SI]: 'kampanja/:campaignId',
      [Shops.SK10MA]: 'kampan/:campaignId',
    },
  },
  campaignPreviewHashAbTestVariant: {
    name: 'campaignPreviewHashAbTestVariant',
    paths: {
      [Shops.CZ10MA]: 'kampan/:campaignId/:previewHash/:abTestVariant',
      [Shops.HR10MA]: 'kampanja/:campaignId/:previewHash/:abTestVariant',
      [Shops.HU20MA]: 'kampany/:campaignId/:previewHash/:abTestVariant',
      [Shops.PL20MA]: 'kampania/:campaignId/:previewHash/:abTestVariant',
      [Shops.SI10SI]: 'kampanja/:campaignId/:previewHash/:abTestVariant',
      [Shops.SK10MA]: 'kampan/:campaignId/:previewHash/:abTestVariant',
    },
  },
  author: {
    name: 'author',
    paths: {
      [Shops.CZ10MA]: 'autor/:authorId',
      [Shops.HR10MA]: 'autor/:authorId',
      [Shops.HU20MA]: 'szerzo/:authorId',
      [Shops.PL20MA]: 'avtor/:authorId',
      [Shops.SI10SI]: 'autor/:authorId',
      [Shops.SK10MA]: 'autor/:authorId',
    },
  },
  brand: {
    name: 'brand',
    paths: {
      [Shops.CZ10MA]: 'znacka/:brandId',
      [Shops.HR10MA]: 'marka/:brandId',
      [Shops.HU20MA]: 'marka/:brandId',
      [Shops.PL20MA]: 'marki/:brandId',
      [Shops.SI10SI]: 'znamke/:brandId',
      [Shops.SK10MA]: 'znacka/:brandId',
    },
  },
  clientCustomerCenter: {
    name: 'clientCustomerCenter',
    componentPath: 'pages/client/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient',
      [Shops.HR10MA]: 'uporabnik',
      [Shops.HU20MA]: 'ugyfel',
      [Shops.PL20MA]: 'klient',
      [Shops.SI10SI]: 'uporabnik',
      [Shops.SK10MA]: 'klient',
    },
  },
  clientMyAccount: {
    name: 'clientMyAccount',
    componentPath: 'pages/account/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/muj-ucet',
      [Shops.HR10MA]: 'uporabnik/moj-racun',
      [Shops.HU20MA]: 'ugyfel/my-account',
      [Shops.PL20MA]: 'klient/my-account',
      [Shops.SI10SI]: 'uporabnik/moj-racun',
      [Shops.SK10MA]: 'klient/my-account',
    },
  },
  clientOrders: {
    name: 'clientOrders',
    componentPath: 'pages/orders/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/objednavky',
      [Shops.HR10MA]: 'uporabnik/narocila',
      [Shops.HU20MA]: 'ugyfel/orders',
      [Shops.PL20MA]: 'klient/orders',
      [Shops.SI10SI]: 'uporabnik/narocila',
      [Shops.SK10MA]: 'klient/orders',
    },
  },
  clientOrderDetail: {
    name: 'clientOrderDetail',
    componentPath: 'pages/orderDetail/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/kc-purchase-detail/:orderId',
      [Shops.HR10MA]: 'uporabnik/kc-purchase-detail/:orderId',
      [Shops.HU20MA]: 'ugyfel/kc-purchase-detail/:orderId',
      [Shops.PL20MA]: 'klient/kc-purchase-detail/:orderId',
      [Shops.SI10SI]: 'uporabnik/kc-purchase-detail/:orderId',
      [Shops.SK10MA]: 'klient/kc-purchase-detail/:orderId',
    },
  },
  reviews: {
    name: 'reviews',
    componentPath: 'pages/reviews/index.vue',
    paths: {
      [Shops.CZ10MA]: 'hodnoceni/',
      [Shops.HR10MA]: 'misljenja/',
      [Shops.HU20MA]: 'ertekelesek/',
      [Shops.PL20MA]: 'recenzje/',
      [Shops.SI10SI]: 'mnenja/',
      [Shops.SK10MA]: 'hodnotenie/',
    },
  },
  clientCardManagement: {
    name: 'clientCardManagement',
    paths: {
      [Shops.CZ10MA]: 'klient/sprava-karet',
      [Shops.HR10MA]: 'uporabnik/',
      [Shops.HU20MA]: 'ugyfel/',
      [Shops.PL20MA]: 'klient/card-management',
      [Shops.SI10SI]: 'uporabnik/',
      [Shops.SK10MA]: 'klient/',
    },
  },
  clientDigitalLicense: {
    name: 'clientDigitalLicense',
    paths: {
      [Shops.CZ10MA]: 'klient/digitalni-licence',
      [Shops.HR10MA]: 'uporabnik/',
      [Shops.HU20MA]: 'ugyfel/',
      [Shops.PL20MA]: 'klient/',
      [Shops.SI10SI]: 'uporabnik/',
      [Shops.SK10MA]: 'klient/digitalne-licencie',
    },
  },
  clientListOfComplaints: {
    name: 'clientListOfComplaints',
    paths: {
      [Shops.CZ10MA]: 'klient/seznam-reklamaci',
      [Shops.HR10MA]: 'uporabnik/complaint-list',
      [Shops.HU20MA]: 'ugyfel/complaint-list',
      [Shops.PL20MA]: 'klient/complaint-list',
      [Shops.SI10SI]: 'uporabnik/seznam-nalogov-vracil',
      [Shops.SK10MA]: 'klient/complaint-list',
    },
  },
  clientLoyalityClubs: {
    name: 'clientLoyalityClubs',
    componentPath: 'pages/clubs/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/vernostni-kluby',
      [Shops.HR10MA]: 'uporabnik/',
      [Shops.HU20MA]: 'ugyfel/',
      [Shops.PL20MA]: 'klient/',
      [Shops.SI10SI]: 'uporabnik/',
      [Shops.SK10MA]: 'klient/vernostne-kluby',
    },
  },
  clientVipClub: {
    name: 'clientVipClub',
    paths: {
      [Shops.CZ10MA]: 'klient/vip-klub',
      [Shops.HR10MA]: 'uporabnik/vip-klub',
      [Shops.HU20MA]: 'ugyfel/vip-klub',
      [Shops.PL20MA]: 'klient/vip-klub',
      [Shops.SI10SI]: 'uporabnik/vip-klub',
      [Shops.SK10MA]: 'klient/vip-klub',
    },
  },
  clientWatchdog: {
    name: 'clientWatchdog',
    componentPath: 'pages/watchdog/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/watchdog',
      [Shops.HR10MA]: 'uporabnik/watchdog',
      [Shops.HU20MA]: 'ugyfel/watchdog',
      [Shops.PL20MA]: 'klient/watchdog',
      [Shops.SI10SI]: 'uporabnik/sledilec',
      [Shops.SK10MA]: 'klient/watchdog',
    },
  },
  clientWishlist: {
    name: 'clientWishlist',
    componentPath: 'pages/wishlist/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/wishlist',
      [Shops.HR10MA]: 'uporabnik/wishlist',
      [Shops.HU20MA]: 'ugyfel/wishlist',
      [Shops.PL20MA]: 'klient/wishlist',
      [Shops.SI10SI]: 'uporabnik/seznami',
      [Shops.SK10MA]: 'klient/wishlist',
    },
  },
  clientWishlistDetail: {
    name: 'clientWishlistDetail',
    componentPath: 'pages/wishlist/detail.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/wishlist-detail/:wishlistId',
      [Shops.HR10MA]: 'uporabnik/wishlist-detail/:wishlistId',
      [Shops.HU20MA]: 'ugyfel/wishlist-detail/:wishlistId',
      [Shops.PL20MA]: 'klient/wishlist-detail/:wishlistId',
      [Shops.SI10SI]: 'uporabnik/wishlist-detail/:wishlistId',
      [Shops.SK10MA]: 'klient/wishlist-detail/:wishlistId',
    },
  },
  publicWishlist: {
    name: 'publicWishlist',
    componentPath: 'pages/wishlist/public.vue',
    paths: {
      [Shops.CZ10MA]: 'wishlists/:wishlistHash',
      [Shops.HR10MA]: 'wishlists/:wishlistHash',
      [Shops.HU20MA]: 'wishlists/:wishlistHash',
      [Shops.PL20MA]: 'wishlists/:wishlistHash',
      [Shops.SI10SI]: 'wishlists/:wishlistHash',
      [Shops.SK10MA]: 'wishlists/:wishlistHash',
    },
  },
  clientFinishAccountDeactivation: {
    name: 'clientFinishAccountDeactivation',
    componentPath: 'pages/finishAccountDeactivation/index.vue',
    paths: {
      [Shops.CZ10MA]: 'klient/zruseni-uctu',
      [Shops.HR10MA]: 'uporabnik/ukinitev-racuna',
      [Shops.HU20MA]: 'ugyfel/user-deactivation',
      [Shops.PL20MA]: 'klient/user-deactivation',
      [Shops.SI10SI]: 'uporabnik/ukinitev-racuna',
      [Shops.SK10MA]: 'klient/user-deactivation',
    },
  },
  loyaltyClubDetail: {
    name: 'loyaltyClubDetail',
    paths: {
      [Shops.CZ10MA]: 'vernostni-klub/:clubUrl',
      [Shops.HR10MA]: 'popisi/:clubUrl',
      [Shops.HU20MA]: 'lapok/:clubUrl',
      [Shops.PL20MA]: 'klient/:clubUrl',
      [Shops.SI10SI]: 'seznami/:clubUrl',
      [Shops.SK10MA]: 'vernostny-klub/:clubUrl',
    },
  },
  orderTrackingSearch: {
    name: 'orderTrackingSearch',
    componentPath: 'pages/delivery/index.vue',
    paths: {
      [Shops.CZ10MA]: 'sledovani-objednavky',
      [Shops.HR10MA]: 'pracenje-narudzbe',
      [Shops.HU20MA]: 'rendeleskovetes',
      [Shops.PL20MA]: 'sledzenie-zamowienia',
      [Shops.SI10SI]: 'sledenje-narocila',
      [Shops.SK10MA]: 'sledovanie-objednavky',
    },
  },
  orderTracking: {
    name: 'orderTracking',
    componentPath: 'pages/delivery/index.vue',
    paths: {
      [Shops.CZ10MA]: 'sledovani-objednavky/:orderId',
      [Shops.HR10MA]: 'pracenje-narudzbe/:orderId',
      [Shops.HU20MA]: 'rendeleskovetes/:orderId',
      [Shops.PL20MA]: 'sledzenie-zamowienia/:orderId',
      [Shops.SI10SI]: 'sledenje-narocila/:orderId',
      [Shops.SK10MA]: 'sledovanie-objednavky/:orderId',
    },
  },
  orderTrackingDetail: {
    name: 'orderTrackingDetail',
    componentPath: 'pages/deliveryDetail/index.vue',
    paths: {
      [Shops.CZ10MA]: 'sledovani-objednavky/detail/:orderId/:deliveryId',
      [Shops.HR10MA]: 'pracenje-narudzbe/detail/:orderId/:deliveryId',
      [Shops.HU20MA]: 'rendeleskovetes/detail/:orderId/:deliveryId',
      [Shops.PL20MA]: 'sledzenie-zamowienia/detail/:orderId/:deliveryId',
      [Shops.SI10SI]: 'sledenje-narocila/detail/:orderId/:deliveryId',
      [Shops.SK10MA]: 'sledovanie-objednavky/detail/:orderId/:deliveryId',
    },
  },
  userChangeEmail: {
    name: 'userChangeEmail',
    componentPath: 'pages/processChangeEmail/index.vue',
    paths: {
      [Shops.CZ10MA]: 'zmena-emailu/processChangeEmail',
      [Shops.HR10MA]: 'promijenite-email/processChangeEmail',
      [Shops.HU20MA]: 'email-modositasa/processChangeEmail',
      [Shops.PL20MA]: 'zmien-email/processChangeEmail',
      [Shops.SI10SI]: 'spremenite-email/processChangeEmail',
      [Shops.SK10MA]: 'zmena-emailu/processChangeEmail',
    },
  },
  subscriptionOptout: {
    name: 'subscriptionOptout',
    componentPath: 'pages/subscriptionOptout/index.vue',
    paths: {
      [Shops.CZ10MA]: 'odhlaseni-odberu/',
      [Shops.HR10MA]: 'iskljucivanje-pretplate/',
      [Shops.HU20MA]: 'leiratkozas/',
      [Shops.PL20MA]: 'rezygnacja-z-subskrypcji/',
      [Shops.SI10SI]: 'odjava-narocnine/',
      [Shops.SK10MA]: 'odhlasenie-odberu/',
    },
  },
  rmaAuthorizedServices: {
    name: 'rmaAuthorizedServices',
    componentPath: 'pages/rmaAuthorizedService/index.vue',
    paths: {
      [Shops.CZ10MA]: 'servisy',
      [Shops.HR10MA]: 'servisy',
      [Shops.HU20MA]: 'servisy',
      [Shops.PL20MA]: 'servisy',
      [Shops.SI10SI]: 'servisy',
      [Shops.SK10MA]: 'servisy',
    },
  },
  complaintsInfoPage: {
    name: 'complaintsInfoPage',
    paths: {
      [Shops.CZ10MA]: 'vraceni-reklamace',
      [Shops.SK10MA]: 'vratenie-reklamacia',
      [Shops.SI10SI]: 'vracilo-blaga',
      [Shops.PL20MA]: 'zwrot-reklamacji',
      [Shops.HU20MA]: 'visszakuldesek-szerviz',
      [Shops.HR10MA]: 'povrat-artikla',
    },
  },
  userForgottenPassword: {
    name: 'userForgottenPassword',
    componentPath: 'pages/passwordReset/index.vue',
    paths: {
      [Shops.CZ10MA]: 'user/password-recovery/recovery',
      [Shops.HR10MA]: 'user/password-recovery/recovery',
      [Shops.HU20MA]: 'user/password-recovery/recovery',
      [Shops.PL20MA]: 'user/password-recovery/recovery',
      [Shops.SI10SI]: 'user/password-recovery/recovery',
      [Shops.SK10MA]: 'user/password-recovery/recovery',
    },
  },
  userRegistration: {
    name: 'userRegistration',
    componentPath: 'pages/registration/index.vue',
    paths: {
      [Shops.CZ10MA]: 'user/register/register',
      [Shops.HR10MA]: 'user/register/register',
      [Shops.HU20MA]: 'user/register/register',
      [Shops.PL20MA]: 'user/register/register',
      [Shops.SI10SI]: 'user/register/register',
      [Shops.SK10MA]: 'user/register/register',
    },
  },
  userRegistrationSent: {
    name: 'userRegistrationSent',
    componentPath: 'pages/registration/sent.vue',
    paths: {
      [Shops.CZ10MA]: 'user/register/sent',
      [Shops.HR10MA]: 'user/register/sent',
      [Shops.HU20MA]: 'user/register/sent',
      [Shops.PL20MA]: 'user/register/sent',
      [Shops.SI10SI]: 'user/register/sent',
      [Shops.SK10MA]: 'user/register/sent',
    },
  },
  userRegistrationFinish: {
    name: 'userRegistrationFinish',
    componentPath: 'pages/registration/finish.vue',
    paths: {
      [Shops.CZ10MA]: 'user/register/finish',
      [Shops.HR10MA]: 'user/register/finish',
      [Shops.HU20MA]: 'user/register/finish',
      [Shops.PL20MA]: 'user/register/finish',
      [Shops.SI10SI]: 'user/register/finish',
      [Shops.SK10MA]: 'user/register/finish',
    },
  },
  userLogin: {
    name: 'userLogin',
    paths: {
      [Shops.CZ10MA]: 'user/auth/login',
      [Shops.HR10MA]: 'user/auth/login',
      [Shops.HU20MA]: 'user/auth/login',
      [Shops.PL20MA]: 'user/auth/login',
      [Shops.SI10SI]: 'user/auth/login',
      [Shops.SK10MA]: 'user/auth/login',
    },
  },
  cart: {
    name: 'cart',
    paths: {
      [Shops.CZ10MA]: 'kosik',
      [Shops.HR10MA]: 'kosarica',
      [Shops.HU20MA]: 'kosar',
      [Shops.PL20MA]: 'koszyk',
      [Shops.SI10SI]: 'kosarica',
      [Shops.SK10MA]: 'kosik',
    },
  },
  personalData: {
    name: 'personalData',
    paths: {
      [Shops.CZ10MA]: 'osobni-udaje',
      [Shops.HR10MA]: 'osobni-podaci',
      [Shops.HU20MA]: 'ugyfel-adatok',
      [Shops.PL20MA]: 'dane-osobowe',
      [Shops.SI10SI]: 'osobni-podaci',
      [Shops.SK10MA]: 'osobne-udaje',
    },
  },
  privacySettings: {
    name: 'privacySettings',
    paths: {
      [Shops.CZ10MA]: 'nastaveni-soukromi',
      [Shops.HR10MA]: 'principi-privatnosti',
      [Shops.HU20MA]: 'adatvedelmi-beallitasok',
      [Shops.PL20MA]: 'ustawienia-prywatnosci',
      [Shops.SI10SI]: 'nastavitve-zasebnosti',
      [Shops.SK10MA]: 'nastavenie-sukromia',
    },
  },
  privacySecurity: {
    name: 'privacySecurity',
    paths: {
      [Shops.CZ10MA]: 'ochrana-soukromi',
      [Shops.HR10MA]: 'principi-privatnosti',
      [Shops.HU20MA]: 'TODO',
      [Shops.PL20MA]: 'TODO',
      [Shops.SI10SI]: 'principi-privatnosti',
      [Shops.SK10MA]: 'ochrana-sukromia',
    },
  },
  bonusSet: {
    name: 'bonusSet',
    paths: {
      [Shops.CZ10MA]: 'akcni-sety/:productUrl',
      [Shops.HR10MA]: 'akcijski-kompleti/:productUrl',
      [Shops.HU20MA]: 'akcios-szettek/:productUrl',
      [Shops.PL20MA]: 'dzialanie-sety/:productUrl',
      [Shops.SI10SI]: 'akcijski-kompleti/:productUrl',
      [Shops.SK10MA]: 'akcne-sety/:productUrl',
    },
  },
  header: {
    name: 'header',
    componentPath: 'pages/header/index.vue',
    paths: {
      [Shops.CZ10MA]: 'header/:shopId',
      [Shops.HR10MA]: 'header/:shopId',
      [Shops.HU20MA]: 'header/:shopId',
      [Shops.PL20MA]: 'header/:shopId',
      [Shops.SI10SI]: 'header/:shopId',
      [Shops.SK10MA]: 'header/:shopId',
    },
  },
  detailById: {
    name: 'detailById',
    paths: {
      [Shops.CZ10MA]: 'id/:productOrVariantId',
      [Shops.HR10MA]: 'id/:productOrVariantId',
      [Shops.HU20MA]: 'id/:productOrVariantId',
      [Shops.PL20MA]: 'id/:productOrVariantId',
      [Shops.SI10SI]: 'id/:productOrVariantId',
      [Shops.SK10MA]: 'id/:productOrVariantId',
    },
  },
  detail: {
    name: 'detail',
    paths: {
      [Shops.CZ10MA]: ':categoryId/:productUrl',
      [Shops.HR10MA]: ':categoryId/:productUrl',
      [Shops.HU20MA]: ':categoryId/:productUrl',
      [Shops.PL20MA]: ':categoryId/:productUrl',
      [Shops.SI10SI]: ':categoryId/:productUrl',
      [Shops.SK10MA]: ':categoryId/:productUrl',
    },
  },
  detailBrand: {
    name: 'detailBrand',
    paths: {
      [Shops.CZ10MA]: 'znacka/:brandId/:categoryId/:productUrl',
      [Shops.HR10MA]: 'marka/:brandId/:categoryId/:productUrl',
      [Shops.HU20MA]: 'marka/:brandId/:categoryId/:productUrl',
      [Shops.PL20MA]: 'marki/:brandId/:categoryId/:productUrl',
      [Shops.SI10SI]: 'znamke/:brandId/:categoryId/:productUrl',
      [Shops.SK10MA]: 'znacka/:categoryId/:productUrl',
    },
  },
  partner: {
    name: 'partner',
    paths: {
      [Shops.CZ10MA]: 'partner/:partnerUrl',
      [Shops.HR10MA]: 'partner/:partnerUrl',
      [Shops.HU20MA]: 'partner/:partnerUrl',
      [Shops.PL20MA]: 'partner/:partnerUrl',
      [Shops.SI10SI]: 'partner/:partnerUrl',
      [Shops.SK10MA]: 'partner/:partnerUrl',
    },
  },
  comparing: {
    name: 'comparing',
    paths: {
      [Shops.CZ10MA]: 'porovnani',
      [Shops.HR10MA]: 'usporedba',
      [Shops.HU20MA]: 'osszehasonlitas',
      [Shops.PL20MA]: 'porownanie',
      [Shops.SI10SI]: 'primerjava',
      [Shops.SK10MA]: 'porovnanie',
    },
  },
  search: {
    name: 'search',
    paths: {
      [Shops.CZ10MA]: 'hledej',
      [Shops.HR10MA]: 'trazenje',
      [Shops.HU20MA]: 'keres',
      [Shops.PL20MA]: 'szukaj',
      [Shops.SI10SI]: 'iskanje',
      [Shops.SK10MA]: 'hladaj',
    },
  },
  licence: {
    name: 'licence',
    paths: {
      [Shops.CZ10MA]: 'licence/:licenceId',
      [Shops.HR10MA]: 'licenca/:licenceId',
      [Shops.HU20MA]: 'jogok/:licenceId',
      [Shops.PL20MA]: 'licencja/:licenceId',
      [Shops.SI10SI]: 'licenca/:licenceId',
      [Shops.SK10MA]: 'licence/:licenceId',
    },
  },
  category: {
    name: 'category',
    paths: {
      [Shops.CZ10MA]: ':url',
      [Shops.HR10MA]: ':url',
      [Shops.HU20MA]: ':url',
      [Shops.PL20MA]: ':url',
      [Shops.SI10SI]: ':url',
      [Shops.SK10MA]: ':url',
    },
  },
  personalDataForms: {
    name: 'personalDataForms',
    componentPath: 'pages/personalDataForms/index.vue',
    paths: {
      [Shops.CZ10MA]: 'sprava-osobnich-udaju',
      [Shops.SK10MA]: 'sprava-osobnych-udajov',
      [Shops.PL20MA]: 'zarzadzanie-danymi-osobowymi',
      [Shops.HU20MA]: 'szemelyes-adatok-kezelese',
      [Shops.HR10MA]: 'uredivanje-osobnih-podataka',
      [Shops.SI10SI]: 'urejanje-osebnih-podatkov',
    },
  },
  personalDataFormsSent: {
    name: 'personalDataFormsSent',
    componentPath: 'pages/personalDataForms/sent.vue',
    paths: {
      [Shops.CZ10MA]: 'sprava-osobnich-udaju/odeslano',
      [Shops.SK10MA]: 'sprava-osobnych-udajov/odoslane',
      [Shops.PL20MA]: 'zarzadzanie-danymi-osobowymi/wyslane',
      [Shops.HU20MA]: 'szemelyes-adatok-kezelese/elkuldve',
      [Shops.HR10MA]: 'uredivanje-osobnih-podataka/poslano',
      [Shops.SI10SI]: 'urejanje-osebnih-podatkov/poslano',
    },
  },
  personalDataFormsVerify: {
    name: 'personalDataFormsVerify',
    componentPath: 'pages/personalDataForms/verify.vue',
    paths: {
      [Shops.CZ10MA]: 'sprava-osobnich-udaju/overeni/:hash',
      [Shops.SK10MA]: 'sprava-osobnych-udajov/overenie/:hash',
      [Shops.PL20MA]: 'zarzadzanie-danymi-osobowymi/weryfikacja/:hash',
      [Shops.HU20MA]: 'szemelyes-adatok-kezelese/visszaigazolas/:hash',
      [Shops.HR10MA]: 'uredivanje-osobnih-podataka/potvrda/:hash',
      [Shops.SI10SI]: 'urejanje-osebnih-podatkov/potrditev/:hash',
    },
    aliases: {
      [Shops.CZ10MA]: 'personal-data-forms/verify/:hash',
      [Shops.SK10MA]: 'personal-data-forms/verify/:hash',
      [Shops.PL20MA]: 'personal-data-forms/verify/:hash',
      [Shops.HU20MA]: 'personal-data-forms/verify/:hash',
      [Shops.HR10MA]: 'personal-data-forms/verify/:hash',
      [Shops.SI10SI]: 'personal-data-forms/verify/:hash',
    },
  },
}

// Helper to get all shop IDs as an array
export const ALL_SHOPS = Object.values(Shops)
